import { inject, Injectable } from '@angular/core';
import { AssetCategory, assetCategoryImagePath } from '@simOn/asset-categories/models';
import { MediaApiService } from '@simOn/common/media';
import { catchError, Observable, of, shareReplay } from 'rxjs';

/**
 * Resolves the download url of a custom asset category image (custom uploaded SVG icon or `CustomImage`).
 *
 * `GetAssetCategories` / `GetAsset` return the category with `imageUrlWithSasToken`, but the asset list and
 * the create / update asset responses return it with only `image.blobPath`. The blob is private, so the badge
 * and the 3D marker cannot load that path directly; this service turns it into a SAS download url via
 * `Media/GenerateBlobUrlForDownload`, cached per blob path.
 */
@Injectable({
  providedIn: 'root'
})
export class AssetCategoryImageService {
  private readonly _mediaApiService = inject(MediaApiService);
  private readonly _cache = new Map<string, Observable<string | null>>();

  /**
   * Emits the image download url: `imageUrlWithSasToken` when the category already carries one,
   * otherwise a url resolved from `image.blobPath`; `null` for categories without a custom image
   * (or when the url cannot be resolved).
   */
  imageUrl(category: AssetCategory | null | undefined): Observable<string | null> {
    if (category?.imageUrlWithSasToken) return of(category.imageUrlWithSasToken);

    const blobPath = assetCategoryImagePath(category);
    if (!blobPath) return of(null);

    let cached = this._cache.get(blobPath);
    if (!cached) {
      cached = this._mediaApiService.generateBlobUrlForDownload(blobPath).pipe(
        catchError((error) => {
          console.warn(`Asset category image url could not be resolved for "${blobPath}":`, error);
          this._cache.delete(blobPath);
          return of(null);
        }),
        shareReplay(1)
      );
      this._cache.set(blobPath, cached);
    }
    return cached;
  }
}
